import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Gallery } from '../types'
import { fetchGalleryById, updateGallery, deleteGallery, setCoverImage, bulkSetPublic, bulkUpdateStatus } from '../api/galleryApi'

/**
 * Single-gallery hooks
 *
 * TODOs:
 * - [ ] Add `useApproveGallery`, `useRejectGallery`, `useTogglePublic` (bulk also in api)
 * - [ ] Add optimistic updates for title/description/region edits
 * - [ ] Ensure error surfaces are user-friendly (optional toast in page)
 * - [ ] Prefetch related images on success to reduce layout jumps
 */

export function useGallery(id: number | undefined) {
  return useQuery<Gallery>({
    queryKey: ['gallery', id],
    queryFn: () => fetchGalleryById(id as number),
    enabled: !!id
  })
}

export function useUpdateGallery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Gallery> }) => updateGallery(id, patch),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['galleries'] })
      qc.invalidateQueries({ queryKey: ['gallery', data.id] })
    }
  })
}

export function useDeleteGallery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteGallery(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['galleries'] })
    }
  })
}

export function useSetCoverImage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, imageUrl, jobImageId }: { id: number; imageUrl: string; jobImageId?: number }) =>
      setCoverImage(id, imageUrl, jobImageId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['gallery', data.id] })
      qc.invalidateQueries({ queryKey: ['galleries'] })
      qc.invalidateQueries({ queryKey: ['gallery-images'] })
    }
  })
}

// Approve/Reject/Toggle-Public single-gallery helpers (wrap bulk for consistency)
export function useApproveGallery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => bulkUpdateStatus([id], 'approved'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['galleries'] })
  })
}

export function useRejectGallery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => bulkUpdateStatus([id], 'rejected'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['galleries'] })
  })
}

export function useTogglePublic() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, makePublic }: { id: number; makePublic: boolean }) => bulkSetPublic([id], makePublic),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['galleries'] })
  })
}
